// @flow
import React, { useContext, useState, useCallback, useEffect } from 'react';
import classNames from 'classnames/bind';
import prop from 'ramda/es/prop';
import assoc from 'ramda/es/assoc';
import identity from 'ramda/es/identity';
import keys from 'ramda/es/keys';
import filter from 'ramda/es/filter';
import length from 'ramda/es/length';
import pipe from 'ramda/es/pipe';
import reduce from 'ramda/es/reduce';

import Modal, { ModalHeader, ModalFooter, ModalContent } from 'app/common/components/Modal';
import { useTranslation } from 'react-i18next';
import ControlsBar from 'app/common/ui-next/controls-bar';
import PlainButton from 'app/common/ui-next/plain-button';
import { HeaderLvl3 } from 'app/common/layouts/markup';
import { Price } from 'app/common/layouts/commonShop';
import { toFixedValueCurrency } from 'app/common/lib/toFixedValue';

import {
  ECartItem,
  ECartItemThumb,
  ECartItemTitle,
  ECartItemContent,
  ECartItemCheck,
  // Stock,
} from 'app/common/layouts/ecart';

import TargetSelectModal from './link/TargetSelectModal';
import IdentifiersModal from './link/IdentifiersModal';
import ItemStatus from '../../ItemStatus';
import PaymentStatus from '../../PaymentStatus';
import { OrderContext, type ReceiverItem, type OrderItem } from '../../OrderData';
import styles from './IssueModal.module.scss';;
import DateFrom from '../../DateFrom';


const cx = classNames.bind(styles);
const defaultCheckedState = {};

type Props = {
  onClose?: () => void,
  timezone: string,
};

const IssueModal = ({ onClose, timezone }: Props) => {
  const { t } = useTranslation('shop');
  const {
    content: { order },
    request,
    loading: {
      issue: loadingIssue,
      receivers: loadingReceivers,
    },
  } = useContext(OrderContext);

  const {
    number,
    payment_status: paymentStatus,
    date,
  } = order;

  const [receiver, setReceiver] = useState<ReceiverItem | null>(null);
  const [linkModal, showLinkModal] = useState(false);
  const toggleSelectModal = useCallback(() => {
    showLinkModal(a => !a);
  }, []);
  const toggleLinkModal = useCallback(() => {
    showLinkModal((a) => {
      if (a) {
        setReceiver(null);
      }
      return !a;
    });
  }, []);

  const [checked, setChecked] = useState(defaultCheckedState);
  const checkedArray = keys(filter(identity, checked));
  const handleCheck = useCallback((e: SyntheticEvent<HTMLInputElement>) => {
    const { value, checked: checked_ } = e.currentTarget;
    setChecked(assoc(value, checked_));
  }, []);

  useEffect(() => {
    setChecked(defaultCheckedState);
  }, [order]);

  const handleIssue = useCallback(async () => {
    const items = checkedArray.map(key => ({
      order_item_id: parseInt(key, 10),
      status: 'issued',
    }));
    await request.issue(items);
  }, [checkedArray, request]);

  const orderItems: Array<OrderItem> = prop('order_items', order) || [];
  const currentStatuses = pipe(
    obj => prop('current_issue_statuses', obj) || [],
    reduce((acc, v) => ({ ...acc, ...v }), {}),
  )(order);

  // Выделить сразу, если элемент один
  useEffect(() => {
    if (orderItems.length === 1) {
      const value = String(orderItems[0].id);
      setChecked(assoc(value, true));
    }
  }, [orderItems]);

  return (
    <Modal toggleModal={onClose} className={styles.root}>
      <ModalHeader>
        {t('issuing_modal_header')}
      </ModalHeader>

      <ModalContent className={styles.goodsInfoContent}>
        <HeaderLvl3 className={styles.goodsInfo}>
          {t('order_in_list_header', { number })}
          <DateFrom date={date} timezone={timezone} />
          <PaymentStatus status={paymentStatus} />
        </HeaderLvl3>
      </ModalContent>

      <ModalContent maxHeight={310}>
        {orderItems.map(({ id: itemId, image, name, price }) => {
          const { status = 'not_issued', datetime } = currentStatuses[itemId];
          const htmlFor = String(itemId);

          return (
            <ECartItem
              id={itemId}
              key={itemId}
              className={cx(styles.goods, { issued: status === 'issued' })}
            >
              <ECartItemCheck
                id={itemId}
                label={name}
                checked={checked[itemId]}
                onChange={handleCheck}
                disabled={status === 'issued'}
              />
              <ECartItemThumb
                imgSrc={prop('standart', image)}
                title={name}
                htmlFor={htmlFor}
              />
              <ECartItemTitle
                className={styles.nameCol}
                htmlFor={htmlFor}
              >
                {name}
                <ItemStatus
                  timezone={timezone}
                  status={status}
                  datetime={datetime}
                />
              </ECartItemTitle>
              <ECartItemContent
                className={styles.priceCol}
                htmlFor={htmlFor}
              >
                <Price>{toFixedValueCurrency(price)}</Price>
              </ECartItemContent>
            </ECartItem>
          );
        })}
      </ModalContent>

      <ModalFooter>
        <ControlsBar right>
          <PlainButton
            disabled={length(checkedArray) !== 1 || loadingIssue}
            onClick={toggleLinkModal}
          >
            {t('issuing_modal_link_bt')}
          </PlainButton>
          <PlainButton
            disabled={length(checkedArray) < 1 || loadingIssue}
            onClick={handleIssue}
          >
            {t('issuing_modal_issue_bt')}
          </PlainButton>
          <PlainButton outline onClick={onClose}>
            {t('issuing_modal_cancel_bt')}
          </PlainButton>
        </ControlsBar>
      </ModalFooter>

      {linkModal && !receiver
        && <TargetSelectModal
          toggleModal={toggleSelectModal}
          onChangeReceiver={setReceiver}
        />}

      {linkModal
        && receiver
        && !loadingReceivers
        && <IdentifiersModal
          toggleModal={toggleLinkModal}
          receiver={receiver}
          selectedProductId={length(checkedArray) === 1 ? parseInt(checkedArray[0], 10) : 0}
        />}
    </Modal>
  );
};

export default IssueModal;
